import Grid from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import { Stack, Typography, SxProps } from '@mui/material';
import { downloadFile } from '@/utils';
import {
  FileTypes,
  FileUploadStatus,
  PointOfInterest,
  useRemoveAttachmentsFromPoiMutation,
  useUpdatePointOfInterestFileMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  ImageType,
  SelectedPointOfInterestDocument,
} from '@/__generated__/graphql';
import { Attachment } from '../types';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { usePoiAttachmentCaption } from '@/hooks/usePoiAttachmentCaption';
import { ListFilesSkeleton } from '@/components/Analysis/shared/MediaUploadsAndViewer/ListFilesSkeleton';
import { useSetAtom } from 'jotai';
import { pendingRemovalIdsAtom } from '@/components/Analysis/modules/pointOfInterest/state/atoms';

type Props = {
  selectedPoiInfo: PointOfInterest;
  attachments: Attachment[];
  allAttachments?: Attachment[];
  setPoiAttachments: (update: Attachment[] | ((prev: Attachment[]) => Attachment[])) => void;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdating?: boolean;
  loading?: boolean;
  location?: string;
  layout?: 'card' | 'list';
  readOnly?: boolean;
  fallbackText?: string;
  disableEnterSave?: boolean;
  onCaptionChange?: (attachmentId: string, caption: string) => void;
  onRemoveAttachments?: (attachmentIds: string[]) => void;
  sx?: SxProps;
};

export const AttachmentsViewer = ({
  attachments = [],
  allAttachments,
  setPoiAttachments,
  selectedPoiInfo,
  setIsUpdating,
  isUpdating,
  loading = false,
  location = '',
  layout = 'card',
  fallbackText,
  readOnly = true,
  disableEnterSave = false,
  onCaptionChange,
  onRemoveAttachments,
  sx,
}: Props) => {
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();
  const { updatePoiAttachmentCaption } = usePoiAttachmentCaption();
  const [removeAttachmentsFromPoi] = useRemoveAttachmentsFromPoiMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [isRemoving, setIsRemoving] = useState(false);
  const setPendingRemovalIds = useSetAtom(pendingRemovalIdsAtom);

  useEffect(() => {
    if (!isUpdating || attachments.length === 0 || !setIsUpdating) return;
    const allComplete = attachments.every((att) => !att.isNew);
    if (allComplete) {
      setIsUpdating(false);
    }
  }, [attachments, isUpdating, setIsUpdating]);

  const onUploadComplete = (index: number, file: Attachment) => {
    if (file.isNew && file.resourcePath) {
      const poiAttachmentUrl = getCloudfrontUrl(file.resourcePath);
      setPoiAttachments((prev) =>
        prev.map((a) =>
          a.uuid === file.uuid
            ? {
                ...a,
                url: poiAttachmentUrl,
                type: file.type,
                isNew: false,
                uploadStatus: FileUploadStatus.Success,
              }
            : a
        )
      );
    }
  };

  const handleDownloadAttachment = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  const handleRemoveAttachment = async (attachmentIndex: number) => {
    if (isRemoving) return;

    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.id) return;

    const attachmentsToRemove: string[] = [currentAttachment.id];
    const otherAttachmentsToRemove = allAttachments?.find(
      (eachAttachment) =>
        eachAttachment.id !== currentAttachment.id &&
        eachAttachment.uuid === `${currentAttachment.uuid}-${ImageType.WhiteBgPdf}` &&
        eachAttachment.imageType === ImageType.WhiteBgPdf
    );
    if (otherAttachmentsToRemove?.id) attachmentsToRemove.push(otherAttachmentsToRemove.id);

    if (onRemoveAttachments) {
      onRemoveAttachments(attachmentsToRemove);
      return;
    }

    setIsRemoving(true);
    setPendingRemovalIds((prev) => new Set([...prev, ...attachmentsToRemove]));
    setPoiAttachments((prev) =>
      prev.filter((attachment) => !attachmentsToRemove.includes(attachment.id ?? ''))
    );

    try {
      await removeAttachmentsFromPoi({
        variables: {
          input: {
            pointOfInterestId: selectedPoiInfo.id,
            fileIds: attachmentsToRemove,
          },
        },
        refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
        awaitRefetchQueries: true,
      });
      showSnackBar('Attachment removed', 'success');
    } catch (error) {
      console.error('Error removing attachment:', error);
      showSnackBar('Failed to remove attachment', 'error');
      setPoiAttachments((prev) => {
        const restored = attachments.filter((a) => attachmentsToRemove.includes(a.id ?? ''));
        return [...prev, ...restored];
      });
      setPendingRemovalIds((prev) => {
        const next = new Set(prev);
        attachmentsToRemove.forEach((id) => next.delete(id));
        return next;
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handelPoiAttachmentUpload = async (fileId: string | undefined) => {
    if (!fileId) {
      console.warn('Attempted to upload attachment with undefined fileId');
      return;
    }
    try {
      await updatePoiFile({
        variables: {
          input: {
            fileId,
            pointOfInterestId: selectedPoiInfo.id,
            uploadStatus: FileUploadStatus.Success,
          },
        },
        refetchQueries: [
          AllPointsOfInterestForInspectionDataLoaderDocument,
          SelectedPointOfInterestDocument,
        ],
        onError: (error) => {
          showSnackBar(error.message, 'error');
        },
      });
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showSnackBar('Failed to update attachment status', 'error');
    }
  };

  const handelPoiImageCaption = async (
    updatedCaption: string | undefined,
    fileId: string | undefined,
    uuid: string | undefined
  ) => {
    if (!fileId) {
      console.warn('Cannot update caption: fileId or uuid is missing.', { fileId, uuid });
      return;
    }
    try {
      await updatePoiAttachmentCaption(selectedPoiInfo.id, uuid, fileId, updatedCaption);
      setPoiAttachments((prev) =>
        prev.map((attachment) =>
          attachment.id === fileId ? { ...attachment, caption: updatedCaption } : attachment
        )
      );
    } catch (error) {
      console.error('Error updating image caption:', error);
      showSnackBar('Failed to update image caption', 'error');
    }
  };

  const fallabackCaptionText = layout === 'list' && readOnly ? '' : fallbackText;

  if (loading) {
    return <ListFilesSkeleton location={location} />;
  }
  if (!loading && attachments.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" mt={6}>
        <Typography variant="body2" color="primary.300">
          No attachments yet
        </Typography>
      </Stack>
    );
  }
  return (
    <Grid
      sx={sx}
      container
      spacing={2}
      columns={layout === 'card' ? 12 : 1}
      id={`${location}-attachments`}
      className="captionPadding"
    >
      <MediaViewer
        mediaFiles={attachments?.filter(
          (file) =>
            file &&
            (file.type.includes('image/') ||
              [FileTypes.Jpeg, FileTypes.Png].includes(file.type as FileTypes))
        )}
        handleDownloadAttachment={handleDownloadAttachment}
        handleRemoveAttachment={handleRemoveAttachment}
      >
        {attachments
          ?.filter((file) => !!file)
          .map((file, index) => (
            <Grid
              key={file.id || file.uuid || file.name || `attachment-${index}`}
              size={layout === 'card' ? 4 : 12}
            >
              <FileDisplay
                referenceId={selectedPoiInfo.id}
                collectionName="poi"
                selectedAttachment={file}
                selectedAttachmentIndex={index}
                showRemoveIcon={false}
                enableUpload={true}
                onUploadComplete={onUploadComplete}
                uploadAttachmentHandler={() => handelPoiAttachmentUpload(file.id)}
                imageCaptureHandler={(caption) =>
                  handelPoiImageCaption(caption, file.id, file.uuid)
                }
                location={location}
                handleSBSMode={() => {}}
                handleRemoveAttachment={handleRemoveAttachment}
                handleDownloadAttachment={handleDownloadAttachment}
                layout={layout}
                isRemovingDisabled={isRemoving}
                readOnly={readOnly}
                fallbackText={fallabackCaptionText}
                disableEnterSave={disableEnterSave}
                onCaptionChange={onCaptionChange}
              />
            </Grid>
          ))}
      </MediaViewer>
    </Grid>
  );
};
